import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Bell, Plus, Users, FileText, CheckSquare, Building2, Loader2, Link2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import ValidatedInput from '../ValidatedInput';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { clientsAPI, tasksAPI, customFieldsAPI, teamAPI, lookupValuesAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import AddressForm from '../AddressForm';
import InvoiceForm from '../InvoiceForm';
import ConditionalField from '../ConditionalField';
import ClientSearchInput from '../ClientSearchInput';

// Helper function to format name to Title Case (First Letter Capital)
const formatToTitleCase = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to format SIN number (XXX-XXX-XXX)
const formatSIN = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Helper function to format phone number ((XXX) XXX-XXXX)
const formatPhoneNumber = (value) => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// Helper function to format email to lowercase
const formatEmail = (value) => {
  if (!value) return '';
  return value.toLowerCase();
};

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Dialog states
  const [clientDialog, setClientDialog] = useState(false);
  const [taskDialog, setTaskDialog] = useState(false);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  
  // Data
  const [clients, setClients] = useState([]);
  const [individualClients, setIndividualClients] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [referralSources, setReferralSources] = useState([]);
  
  // New Client form
  const [newClient, setNewClient] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: { line1: '', line2: '', city: '', province: '', country: '', postalCode: '' },
    type: 'Individual',
    status: 'active',
    industry: '',
    referralSource: '',
    // Individual fields
    sin: '',
    dateOfBirth: '',
    maritalStatus: '',
    spouse: { name: '', email: '', phone: '', sin: '', dateOfBirth: '' },
    children: [],
    gstHstNumber: '',
    payrollNumber: '',
    // Corporation fields
    businessNumber: '',
    ownerId: '',
    yearEnd: '',
    customFields: {}
  });
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    clientId: '',
    client: '',
    assignedTo: '',
    dueDate: '',
    priority: 'medium',
    status: 'pending'
  });

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({});
      const allClients = response.data.clients || [];
      setClients(allClients);
      setIndividualClients(allClients.filter(c => c.type === 'Individual'));
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      // Fetch industries and referral sources from lookup values API
      const response = await lookupValuesAPI.getAll();
      setIndustries(response.data.industries || []);
      setReferralSources(response.data.referralSources || []);
    } catch (error) {
      console.error('Error fetching lookup values:', error);
      // Fallback defaults
      setIndustries(['Dentistry', 'Trucking', 'Immigration', 'Professional Services', 'Retail', 'Real Estate', 'Healthcare', 'Technology', 'Manufacturing', 'Construction', 'Other']);
      setReferralSources(['Google', 'Instagram', 'Client Referral', 'Immigration Office', 'Dentist Network', 'Website', 'LinkedIn', 'Walk-in', 'Other']);
    }
  };

  const fetchCustomFields = async () => {
    try {
      const response = await customFieldsAPI.getAll();
      setCustomFields(response.data.fields || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  // Filter custom fields based on client type
  const getApplicableCustomFields = () => {
    const clientType = newClient.type;
    return customFields.filter(field => {
      if (!field.appliesTo || field.appliesTo.length === 0) return true;
      return field.appliesTo.includes(clientType);
    });
  };

  const handleCreateClient = async () => {
    if (!newClient.name || !newClient.email) {
      toast({ title: 'Error', description: 'Name and email are required', variant: 'destructive' });
      return;
    }
    
    try {
      // Determine if spouse/children data should be included (not for single marital status)
      const shouldIncludeFamily = newClient.type === 'Individual' && 
        newClient.maritalStatus && 
        newClient.maritalStatus !== 'Single';
      
      const clientData = {
        ...newClient,
        tags: [],
        openTasks: 0,
        documents: 0,
        revenue: 0,
        customFields: newClient.customFields || {},
        // Clean up spouse data - only include if marital status is not Single
        spouse: shouldIncludeFamily && newClient.spouse?.name ? newClient.spouse : null,
        children: shouldIncludeFamily ? newClient.children : [],
        // Clean up address
        address: newClient.address.line1 ? newClient.address : null
      };
      
      const response = await clientsAPI.create(clientData);
      toast({ title: 'Success', description: `Client "${newClient.name}" created` });
      setClientDialog(false);
      resetClientForm();
      
      if (response.data._id) {
        navigate(`/clients/${response.data._id}`);
      }
    } catch (error) {
      console.error('Error creating client:', error);
      toast({ title: 'Error', description: 'Failed to create client', variant: 'destructive' });
    }
  };

  const resetClientForm = () => {
    setNewClient({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: { line1: '', line2: '', city: '', province: '', country: '', postalCode: '' },
      type: 'Individual',
      status: 'active',
      industry: '',
      referralSource: '',
      sin: '',
      dateOfBirth: '',
      maritalStatus: '',
      spouse: { name: '', email: '', phone: '', sin: '', dateOfBirth: '' },
      children: [],
      gstHstNumber: '',
      payrollNumber: '',
      businessNumber: '',
      ownerId: '',
      yearEnd: '',
      customFields: {}
    });
  };

  const addChild = () => {
    setNewClient(prev => ({
      ...prev,
      children: [...prev.children, { name: '', dateOfBirth: '', sin: '' }]
    }));
  };

  const removeChild = (index) => {
    setNewClient(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const updateChild = (index, field, value) => {
    setNewClient(prev => ({
      ...prev,
      children: prev.children.map((child, i) => 
        i === index ? { ...child, [field]: value } : child
      )
    }));
  };

  // Fetch custom fields when client dialog opens
  useEffect(() => {
    if (clientDialog) {
      fetchCustomFields();
      fetchCompanySettings();
    }
  }, [clientDialog]);

  // Fetch team members when task dialog opens  
  useEffect(() => {
    if (taskDialog) {
      fetchTeamMembers();
    }
  }, [taskDialog]);

  // Global search with debounce
  const debounceSearch = useCallback(() => {
    let timeoutId;
    return (query) => {
      clearTimeout(timeoutId);
      if (!query || query.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      setSearchLoading(true);
      timeoutId = setTimeout(async () => {
        try {
          const response = await clientsAPI.search(query);
          setSearchResults(response.data.results || []);
          setShowSearchResults(true);
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    };
  }, []);

  const debouncedSearch = useCallback(debounceSearch(), [debounceSearch]);

  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchResultClick = (result) => {
    setShowSearchResults(false);
    setSearchQuery('');
    navigate(`/clients/${result._id}`);
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.clientId || !newTask.assignedTo || !newTask.dueDate) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    
    try {
      const taskData = {
        ...newTask,
        dueDate: new Date(newTask.dueDate).toISOString(),
        tags: []
      };
      
      await tasksAPI.create(taskData);
      toast({ title: 'Success', description: `Task "${newTask.title}" created` });
      setTaskDialog(false);
      setNewTask({ title: '', description: '', clientId: '', client: '', assignedTo: '', dueDate: '', priority: 'medium', status: 'pending' });
      navigate('/tasks');
    } catch (error) {
      console.error('Error creating task:', error);
      toast({ title: 'Error', description: 'Failed to create task', variant: 'destructive' });
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    setNewTask(prev => ({ ...prev, clientId, client: client?.name || '' }));
  };

  const updateSpouse = (field, value) => {
    setNewClient(prev => ({
      ...prev,
      spouse: { ...prev.spouse, [field]: value }
    }));
  };

  return (
    <header className="fixed top-0 left-64 right-0 z-10 h-16 border-b bg-white">
      <div className="flex h-full items-center justify-between px-6">
        {/* Search Bar with Results Dropdown */}
        <div className="flex-1 max-w-xl relative" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search clients by name, email or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              className="pl-10 w-full"
              data-testid="global-search-input"
            />
            {searchLoading && (
              <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 animate-spin" />
            )}
          </div>
          
          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-96 overflow-y-auto z-50" data-testid="search-results-dropdown">
              {searchResults.map((result) => (
                <div
                  key={result._id}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  onClick={() => handleSearchResultClick(result)}
                  data-testid={`search-result-${result._id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-semibold">
                        {result.name?.charAt(0) || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{result.name}</p>
                          {result.clientCode && (
                            <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                              {result.clientCode}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{result.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {result.type}
                      </Badge>
                      {result.matchType === 'linked' && (
                        <Badge className="bg-purple-100 text-purple-800 text-xs gap-1">
                          <Link2 className="h-3 w-3" />
                          Linked
                        </Badge>
                      )}
                      {result.matchType === 'spouse' && (
                        <Badge className="bg-pink-100 text-pink-800 text-xs">
                          Spouse Match
                        </Badge>
                      )}
                    </div>
                  </div>
                  {result.linkedTo && (
                    <p className="mt-1 text-xs text-gray-500 ml-11">
                      Linked to: {result.linkedTo}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {showSearchResults && searchQuery.length >= 2 && searchResults.length === 0 && !searchLoading && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-gray-500 z-50">
              No results found for "{searchQuery}"
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="gap-2" data-testid="new-dropdown-btn">
                <Plus className="h-4 w-4" />
                New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem 
                onClick={() => { fetchClients(); setClientDialog(true); }}
                className="cursor-pointer"
              >
                <Users className="h-4 w-4 mr-2" />
                New Client
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => { fetchClients(); setTaskDialog(true); }}
                className="cursor-pointer"
              >
                <CheckSquare className="h-4 w-4 mr-2" />
                New Task
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setInvoiceDialog(true)}
                className="cursor-pointer"
              >
                <FileText className="h-4 w-4 mr-2" />
                New Invoice
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <button 
            className="relative rounded-lg p-2 text-gray-600 hover:bg-gray-100 transition-colors"
            onClick={() => navigate('/notifications')}
          >
            <Bell className="h-5 w-5" />
            <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500" />
          </button>
        </div>
      </div>

      {/* New Client Dialog */}
      <Dialog open={clientDialog} onOpenChange={setClientDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Client Type *</Label>
                <Select value={newClient.type} onValueChange={(v) => setNewClient(prev => ({ ...prev, type: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Individual">Individual</SelectItem>
                    <SelectItem value="Corporation">Corporation</SelectItem>
                    <SelectItem value="Others">Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <ValidatedInput
                  placeholder={newClient.type === 'Corporation' ? 'Company name' : 'Full name'}
                  value={newClient.name}
                  onChange={(e) => setNewClient(prev => ({ ...prev, name: formatToTitleCase(e.target.value) }))}
                  validationType="name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <ValidatedInput
                  type="email"
                  placeholder="email@example.com"
                  value={newClient.email}
                  onChange={(e) => setNewClient(prev => ({ ...prev, email: formatEmail(e.target.value) }))}
                  validationType="email"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <ValidatedInput
                  placeholder="(647) 212-0990"
                  value={newClient.phone}
                  onChange={(e) => setNewClient(prev => ({ ...prev, phone: formatPhoneNumber(e.target.value) }))}
                  validationType="phone"
                />
              </div>
            </div>

            {/* Contact Person - for Corporations */}
            {newClient.type === 'Corporation' && (
              <div className="space-y-2">
                <Label>Contact Person</Label>
                <ValidatedInput
                  placeholder="Primary contact name"
                  value={newClient.contactPerson}
                  onChange={(e) => setNewClient(prev => ({ ...prev, contactPerson: formatToTitleCase(e.target.value) }))}
                  validationType="name"
                  data-testid="contact-person-input"
                />
                <p className="text-xs text-gray-500">The main point of contact for this corporation</p>
              </div>
            )}

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-4">Address</h3>
              <AddressForm
                address={newClient.address}
                onChange={(addr) => setNewClient(prev => ({ ...prev, address: addr }))}
              />
            </div>

            {/* Industry and Referral Source */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-4">Additional Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Select value={newClient.industry || 'none'} onValueChange={(v) => setNewClient(prev => ({ ...prev, industry: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select industry" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Referral Source</Label>
                  <Select value={newClient.referralSource || 'none'} onValueChange={(v) => setNewClient(prev => ({ ...prev, referralSource: v === 'none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select referral source" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not specified</SelectItem>
                      {referralSources.map((src) => (
                        <SelectItem key={src} value={src}>{src}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Individual-specific fields */}
            {newClient.type === 'Individual' && (
              <>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Individual Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={newClient.dateOfBirth}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setNewClient(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Marital Status</Label>
                      <Select 
                        value={newClient.maritalStatus || 'none'} 
                        onValueChange={(v) => setNewClient(prev => ({ ...prev, maritalStatus: v === 'none' ? '' : v }))}
                      >
                        <SelectTrigger><SelectValue placeholder="Select marital status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not specified</SelectItem>
                          <SelectItem value="Single">Single</SelectItem>
                          <SelectItem value="Married">Married</SelectItem>
                          <SelectItem value="Common Law">Common Law</SelectItem>
                          <SelectItem value="Widowed">Widowed</SelectItem>
                          <SelectItem value="Divorced">Divorced</SelectItem>
                          <SelectItem value="Separated">Separated</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="space-y-2">
                      <Label>SIN</Label>
                      <ValidatedInput
                        placeholder="123-456-789"
                        value={newClient.sin}
                        onChange={(e) => setNewClient(prev => ({ ...prev, sin: formatSIN(e.target.value) }))}
                        validationType="sin"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>GST/HST Number</Label>
                      <Input
                        placeholder="123456789RT0001"
                        value={newClient.gstHstNumber}
                        onChange={(e) => setNewClient(prev => ({ ...prev, gstHstNumber: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Payroll Number</Label>
                      <Input
                        placeholder="RP0001"
                        value={newClient.payrollNumber}
                        onChange={(e) => setNewClient(prev => ({ ...prev, payrollNumber: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>

                {/* Spouse Information - Only show if marital status is NOT Single */}
                {newClient.maritalStatus && newClient.maritalStatus !== 'Single' && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-4">Spouse Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Spouse Name</Label>
                        <ValidatedInput
                          placeholder="Spouse full name"
                          value={newClient.spouse.name}
                          onChange={(e) => updateSpouse('name', formatToTitleCase(e.target.value))}
                          validationType="name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse Date of Birth</Label>
                        <Input
                          type="date"
                          value={newClient.spouse.dateOfBirth || ''}
                          max={new Date().toISOString().split('T')[0]}
                          onChange={(e) => updateSpouse('dateOfBirth', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse SIN</Label>
                        <ValidatedInput
                          placeholder="123-456-789"
                          value={newClient.spouse.sin}
                          onChange={(e) => updateSpouse('sin', formatSIN(e.target.value))}
                          validationType="sin"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse Email</Label>
                        <ValidatedInput
                          type="email"
                          placeholder="spouse@example.com"
                          value={newClient.spouse.email}
                          onChange={(e) => updateSpouse('email', formatEmail(e.target.value))}
                          validationType="email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Spouse Phone</Label>
                        <ValidatedInput
                          placeholder="(647) 212-0990"
                          value={newClient.spouse.phone}
                          onChange={(e) => updateSpouse('phone', formatPhoneNumber(e.target.value))}
                          validationType="phone"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Children Information - Only show if marital status is NOT Single */}
                {newClient.maritalStatus && newClient.maritalStatus !== 'Single' && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">Children Information</h3>
                      <Button type="button" variant="outline" size="sm" onClick={addChild} className="gap-1">
                        <Plus className="h-3 w-3" /> Add Child
                      </Button>
                    </div>
                    {newClient.children.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No children added. Click "Add Child" to add.</p>
                    ) : (
                      <div className="space-y-4">
                        {newClient.children.map((child, index) => (
                          <div key={index} className="p-4 border rounded-lg bg-gray-50">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">Child {index + 1}</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeChild(index)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Name</Label>
                                <ValidatedInput
                                  placeholder="Child's name"
                                  value={child.name}
                                  onChange={(e) => updateChild(index, 'name', formatToTitleCase(e.target.value))}
                                  validationType="name"
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Date of Birth</Label>
                                <Input
                                  type="date"
                                  value={child.dateOfBirth || ''}
                                  max={new Date().toISOString().split('T')[0]}
                                  onChange={(e) => updateChild(index, 'dateOfBirth', e.target.value)}
                                  className="h-9"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">SIN</Label>
                                <ValidatedInput
                                  placeholder="123-456-789"
                                  value={child.sin || ''}
                                  onChange={(e) => updateChild(index, 'sin', formatSIN(e.target.value))}
                                  validationType="sin"
                                  className="h-9"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Corporation-specific fields */}
            {newClient.type === 'Corporation' && (
              <>
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Corporation Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Business Number (BN)</Label>
                      <Input
                        placeholder="123456789"
                        value={newClient.businessNumber}
                        onChange={(e) => setNewClient(prev => ({ ...prev, businessNumber: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Year End</Label>
                      <Select value={newClient.yearEnd} onValueChange={(v) => setNewClient(prev => ({ ...prev, yearEnd: v }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select year end" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="January 31">January 31</SelectItem>
                          <SelectItem value="February 28">February 28</SelectItem>
                          <SelectItem value="March 31">March 31</SelectItem>
                          <SelectItem value="April 30">April 30</SelectItem>
                          <SelectItem value="May 31">May 31</SelectItem>
                          <SelectItem value="June 30">June 30</SelectItem>
                          <SelectItem value="July 31">July 31</SelectItem>
                          <SelectItem value="August 31">August 31</SelectItem>
                          <SelectItem value="September 30">September 30</SelectItem>
                          <SelectItem value="October 31">October 31</SelectItem>
                          <SelectItem value="November 30">November 30</SelectItem>
                          <SelectItem value="December 31">December 31</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>GST/HST Number</Label>
                      <Input
                        placeholder="123456789RT0001"
                        value={newClient.gstHstNumber}
                        onChange={(e) => setNewClient(prev => ({ ...prev, gstHstNumber: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Payroll Number</Label>
                      <Input
                        placeholder="RP0001"
                        value={newClient.payrollNumber}
                        onChange={(e) => setNewClient(prev => ({ ...prev, payrollNumber: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <Label>Owner (Individual Client)</Label>
                    <ClientSearchInput
                      value={newClient.ownerId}
                      onSelect={(id, name) => setNewClient(prev => ({ ...prev, ownerId: id }))}
                      placeholder="Search individual owner..."
                      typeFilter="Individual"
                      showPhone={true}
                      allowClear={true}
                      data-testid="header-owner-search-input"
                    />
                    <p className="text-xs text-gray-500">Link this corporation to an existing individual client</p>
                  </div>
                </div>
              </>
            )}

            {/* Custom Fields Section */}
            {getApplicableCustomFields().length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-4">Custom Fields</h3>
                <div className="grid grid-cols-2 gap-4">
                  {getApplicableCustomFields().map((field) => (
                    <div key={field._id} className={`space-y-2 ${(field.type === 'conditional_number' || field.type === 'conditional_dropdown') ? 'col-span-2' : ''}`}>
                      <Label>{field.name} {field.required && '*'}</Label>
                      {(field.type === 'conditional_number' || field.type === 'conditional_dropdown') && (
                        <ConditionalField
                          field={field}
                          value={newClient.customFields[field.fieldId]}
                          onChange={(v) => setNewClient(prev => ({
                            ...prev,
                            customFields: { ...prev.customFields, [field.fieldId]: v }
                          }))}
                        />
                      )}
                      {field.type === 'text' && (
                        <Input
                          placeholder={field.name}
                          value={newClient.customFields[field.fieldId] || ''}
                          onChange={(e) => setNewClient(prev => ({
                            ...prev,
                            customFields: { ...prev.customFields, [field.fieldId]: e.target.value }
                          }))}
                        />
                      )}
                      {field.type === 'number' && (
                        <Input
                          type="number"
                          placeholder={field.name}
                          value={newClient.customFields[field.fieldId] || ''}
                          onChange={(e) => setNewClient(prev => ({
                            ...prev,
                            customFields: { ...prev.customFields, [field.fieldId]: e.target.value }
                          }))}
                        />
                      )}
                      {field.type === 'date' && (
                        <Input
                          type="date"
                          value={newClient.customFields[field.fieldId] || ''}
                          onChange={(e) => setNewClient(prev => ({
                            ...prev,
                            customFields: { ...prev.customFields, [field.fieldId]: e.target.value }
                          }))}
                        />
                      )}
                      {(field.type === 'select' || field.type === 'dropdown') && field.options && (
                        <Select
                          value={newClient.customFields[field.fieldId] || ''}
                          onValueChange={(v) => setNewClient(prev => ({
                            ...prev,
                            customFields: { ...prev.customFields, [field.fieldId]: v }
                          }))}
                        >
                          <SelectTrigger><SelectValue placeholder={`Select ${field.name}`} /></SelectTrigger>
                          <SelectContent>
                            {field.options.map((opt) => {
                              const optValue = typeof opt === 'string' ? opt : opt.label;
                              const optKey = typeof opt === 'string' ? opt : opt.id;
                              return (
                                <SelectItem key={optKey} value={optValue}>{optValue}</SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button onClick={handleCreateClient} className="flex-1">Create Client</Button>
              <Button variant="outline" onClick={() => setClientDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Task Dialog */}
      <Dialog open={taskDialog} onOpenChange={setTaskDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                placeholder="Task title"
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Task description"
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Client *</Label>
              <ClientSearchInput
                value={newTask.clientId}
                onSelect={(clientId, clientName, client) => {
                  setNewTask(prev => ({ 
                    ...prev, 
                    clientId,
                    client: clientName
                  }));
                }}
                placeholder="Search clients..."
              />
            </div>
            <div className="space-y-2">
              <Label>Assigned To *</Label>
              <Select value={newTask.assignedTo} onValueChange={(v) => setNewTask(prev => ({ ...prev, assignedTo: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map((member) => (
                    <SelectItem key={member._id} value={member._id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newTask.priority} onValueChange={(v) => setNewTask(prev => ({ ...prev, priority: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreateTask} className="flex-1">Create Task</Button>
              <Button variant="outline" onClick={() => setTaskDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Invoice Dialog */}
      <InvoiceForm
        open={invoiceDialog}
        onOpenChange={setInvoiceDialog}
        onSuccess={() => navigate('/invoices')}
      />
    </header>
  );
};

export default Header;
